import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ChevronRight, AlertCircle, Activity } from 'lucide-react';
import MobilitySilhouette, { colorForScore } from './MobilitySilhouette';

/**
 * MobilityCard — compact dashboard hero. Shows a small front+back
 * anatomical body tinted per joint region, overall score, and the top
 * asymmetry / focus-area chips. The whole card is a Link to /mobility
 * for the deep dive.
 *
 * Camera-required: when no baseline exists, the empty state CTAs into
 * /assessment.
 *
 * Backwards-compat: file kept as both default and named export under
 * the legacy `MovementBaselineCard` name (via the shim file) so existing
 * imports stay green.
 */

const NoBaseline = () => (
  <Link
    to="/assessment"
    className="hpme-card p-6 block hover:border-primary/60 transition-colors"
    data-testid="movement-baseline-empty"
  >
    <div className="hpme-card-corner" />
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-sm bg-primary/20 flex items-center justify-center flex-shrink-0">
        <Sparkles className="w-6 h-6 text-primary" />
      </div>
      <div className="flex-1">
        <p className="hpme-label mb-1">MOBILITY CHECK</p>
        <h3 className="font-secondary font-bold text-xl tracking-tight mb-2">Map how your body moves</h3>
        <p className="text-zinc-400 text-sm mb-3">
          8 gentle camera-tracked checks · about 5 minutes. So I can build plans that work with your body, not against it.
        </p>
        <span className="text-primary text-sm font-semibold flex items-center gap-1">
          Start <ChevronRight className="w-4 h-4" />
        </span>
      </div>
    </div>
  </Link>
);

const AsymmetryRow = ({ flag }) => (
  <div className="flex items-center gap-2 text-[11px] py-1 px-2 rounded-sm bg-amber-500/10 border border-amber-500/30">
    <span className="text-amber-200/90 flex-shrink-0">{flag.test_name}</span>
    <span className="ml-auto font-secondary font-bold text-amber-200 whitespace-nowrap text-[11px]">
      L {flag.left_score}<span className="text-amber-500/60 mx-1">·</span>R {flag.right_score}
      <span className="ml-1.5 text-amber-400">Δ{flag.delta}</span>
    </span>
  </div>
);

const MobilityCard = ({ baseline, needsRetest, nextRetestDue }) => {
  if (!baseline) return <NoBaseline />;

  const regionScores = baseline.region_scores || {};
  const asymmetry = baseline.asymmetry_flags || [];
  const overall = baseline.overall_mobility || 0;
  const weakest = baseline.weakest_regions || [];

  return (
    <Link to="/mobility" className="block group" data-testid="mobility-card-link">
      <div className="hpme-card p-5 relative transition-colors group-hover:border-primary/40" data-testid="movement-baseline-card">
        <div className="hpme-card-corner" />

        {/* Header row */}
        <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
          <div>
            <p className="hpme-label mb-0.5 flex items-center gap-1.5">
              <Activity className="w-3 h-3 text-primary" /> MOBILITY &amp; ROM
            </p>
            <p className="font-secondary font-bold text-2xl tracking-tight leading-none">
              <span style={{ color: colorForScore(overall) }}>{overall}</span>
              <span className="text-zinc-500 text-sm ml-1">/ 100</span>
            </p>
            <p className="text-[10px] text-zinc-600 mt-1">
              Mapped {new Date(baseline.taken_at).toLocaleDateString()}
              {nextRetestDue && !needsRetest && <> · next retest {new Date(nextRetestDue).toLocaleDateString()}</>}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {needsRetest && (
              <span className="text-[10px] uppercase font-secondary font-bold px-2 py-1 rounded-sm bg-yellow-500/20 text-yellow-300 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Retest due
              </span>
            )}
            <span className="text-xs text-primary flex items-center gap-1 group-hover:underline">
              Open map <ChevronRight className="w-3 h-3" />
            </span>
          </div>
        </div>

        {/* Body row — compact front+back at small size, info to the right (constrained) */}
        <div className="grid md:grid-cols-[260px_minmax(0,1fr)] gap-5 items-start">
          <div className="flex justify-center" data-testid="dashboard-mobility-body">
            <div className="w-full max-w-[240px] h-[200px] pointer-events-none">
              <MobilitySilhouette regionScores={regionScores} asymmetryFlags={asymmetry} size="sm" showBack interactive={false} />
            </div>
          </div>

          <div className="space-y-3 min-w-0 max-w-[480px]">
            {asymmetry.length > 0 ? (
              <div>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">
                  ASYMMETRY · L vs R · top {Math.min(2, asymmetry.length)}
                </p>
                <div className="space-y-1">
                  {asymmetry.slice(0, 2).map(f => (
                    <AsymmetryRow key={f.test_id} flag={f} />
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-xs text-zinc-500">No asymmetry flags. Sides are balanced.</p>
            )}

            {weakest.length > 0 && (
              <div>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">
                  FOCUS AREAS · driving your next plan
                </p>
                <div className="flex gap-1 flex-wrap">
                  {weakest.slice(0, 4).map(k => (
                    <span key={k} className="px-2 py-1 rounded-sm bg-yellow-500/15 text-yellow-300 text-[10px] uppercase font-semibold whitespace-nowrap">
                      {k.replace('_L', ' L').replace('_R', ' R').replace('_', ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <p className="text-[10px] text-zinc-600 pt-1">
              Joints we measure: shoulder · trunk · hip · knee · ankle · neck (L &amp; R)
            </p>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default MobilityCard;
export { MobilityCard };
